import { useState } from "react";
import type { ArgSpec } from "../registry";

export type ArgFormValues = Record<string, unknown>;

type Props = {
  spec: readonly ArgSpec[];
  values: ArgFormValues;
  onChange: (values: ArgFormValues) => void;
  /** Pre-fills contactId/participantId fields from the currently selected contact. */
  selectedContactId?: string;
  /** Prefix for input ids so many forms on one page do not collide. */
  idPrefix?: string;
};

/** Per-field JSON parse errors, keyed by field name. */
type JsonErrors = Record<string, string | undefined>;

/**
 * Renders one form control per {@link ArgSpec}, matching the type to the right native input:
 * enum -> select, boolean -> checkbox, number -> number input, json -> textarea (parsed on
 * change, with an inline error left visible instead of silently discarding bad input),
 * everything else -> text.
 */
export function ArgForm({
  spec,
  values,
  onChange,
  selectedContactId,
  idPrefix = "arg",
}: Props): React.JSX.Element {
  const [jsonText, setJsonText] = useState<Record<string, string>>({});
  const [jsonErrors, setJsonErrors] = useState<JsonErrors>({});

  if (spec.length === 0) {
    return <p className="arg-form-empty">No arguments.</p>;
  }

  const setValue = (k: string, v: unknown): void => {
    onChange({ ...values, [k]: v });
  };

  return (
    <div className="arg-form">
      {spec.map((a) => (
        <div className="arg-field" key={a.k}>
          <label htmlFor={`${idPrefix}-${a.k}`}>
            {a.k}
            {a.required ? <span className="arg-required">*</span> : null}
          </label>
          {renderControl(a)}
          {jsonErrors[a.k] ? (
            <p className="arg-error">{jsonErrors[a.k]}</p>
          ) : null}
        </div>
      ))}
    </div>
  );

  function renderControl(a: ArgSpec): React.JSX.Element {
    const id = `${idPrefix}-${a.k}`;
    if (a.type === "boolean") {
      return (
        <input
          id={id}
          type="checkbox"
          checked={Boolean(values[a.k])}
          onChange={(e) => setValue(a.k, e.target.checked)}
        />
      );
    }
    if (a.type === "number") {
      return (
        <input
          id={id}
          type="number"
          value={typeof values[a.k] === "number" ? (values[a.k] as number) : ""}
          onChange={(e) =>
            setValue(
              a.k,
              e.target.value === "" ? undefined : Number(e.target.value),
            )
          }
        />
      );
    }
    if (a.type === "enum") {
      return (
        <select
          id={id}
          value={typeof values[a.k] === "string" ? (values[a.k] as string) : ""}
          onChange={(e) => setValue(a.k, e.target.value || undefined)}
        >
          <option value="">(choose)</option>
          {(a.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    if (a.type === "json") {
      const text =
        jsonText[a.k] ??
        (values[a.k] !== undefined ? safeStringifyDefault(values[a.k]) : "");
      return (
        <textarea
          id={id}
          rows={3}
          value={text}
          placeholder={
            a.default !== undefined
              ? safeStringifyDefault(a.default)
              : "JSON value"
          }
          onChange={(e) => {
            const raw = e.target.value;
            setJsonText((prev) => ({ ...prev, [a.k]: raw }));
            if (raw.trim() === "") {
              setJsonErrors((prev) => ({ ...prev, [a.k]: undefined }));
              setValue(a.k, undefined);
              return;
            }
            try {
              const parsed: unknown = JSON.parse(raw);
              setJsonErrors((prev) => ({ ...prev, [a.k]: undefined }));
              setValue(a.k, parsed);
            } catch (err) {
              setJsonErrors((prev) => ({
                ...prev,
                [a.k]: err instanceof Error ? err.message : "invalid JSON",
              }));
            }
          }}
        />
      );
    }
    // contactId / participantId / string
    const fillable = a.type === "contactId" && selectedContactId;
    return (
      <div className="arg-text-row">
        <input
          id={id}
          type="text"
          value={typeof values[a.k] === "string" ? (values[a.k] as string) : ""}
          onChange={(e) => setValue(a.k, e.target.value)}
        />
        {fillable ? (
          <button
            type="button"
            onClick={() => setValue(a.k, selectedContactId)}
          >
            Use selected
          </button>
        ) : null}
      </div>
    );
  }
}

function safeStringifyDefault(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}
